import { useNavigate } from 'react-router-dom';
import { useForm } from '../../hooks/useForm';
import { FormField, FormSelect, FormTextArea, SmartFormActions } from '../shared/form';
import { MessageRoute } from '../../types';
import { OperationsTagInput } from './OperationsTagInput';
import { InputMappingEditor } from './InputMappingEditor';

export interface MessageRouteFormData {
  name: string;
  description: string;
  active: boolean;
  priority: number | string;
  channelPattern: string;
  entity: string;
  operations: string[];
  source: string;
  excludeSources: string[];
  endpointPath: string;
  endpointMethod: 'POST' | 'GET';
  cooldownMs: number | string;
  stopOnMatch: boolean;
  inputMapping: Record<string, unknown>;
}

interface MessageRouteFormProps {
  initialData?: Partial<MessageRoute>;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  onCancel?: () => void;
  mode?: 'create' | 'edit';
}

const ENDPOINT_METHOD_OPTIONS = [
  { value: 'POST', label: 'POST' },
  { value: 'GET', label: 'GET' },
];

const COMMON_OPERATIONS = ['create', 'update', 'delete', 'publish'];

export function MessageRouteForm({
  initialData,
  onSubmit,
  onCancel,
  mode = 'create',
}: MessageRouteFormProps) {
  const navigate = useNavigate();

  const {
    values,
    errors,
    isSubmitting,
    isDirty,
    isValid,
    handleChange,
    setFieldValue,
    handleSubmit,
  } = useForm<MessageRouteFormData>({
    initialValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      active: initialData?.active ?? true,
      priority: initialData?.priority ?? 100,
      channelPattern: initialData?.channelPattern || '',
      entity: initialData?.entity || '',
      operations: initialData?.operations || [],
      source: initialData?.source || '',
      excludeSources: initialData?.excludeSources || [],
      endpointPath: initialData?.endpointPath || '',
      endpointMethod: initialData?.endpointMethod || 'POST',
      cooldownMs: initialData?.cooldownMs ?? 60000,
      stopOnMatch: initialData?.stopOnMatch ?? false,
      inputMapping: initialData?.inputMapping || {},
    },
    requiredFields: mode === 'create'
      ? ['name', 'channelPattern', 'operations', 'endpointPath']
      : ['channelPattern', 'operations', 'endpointPath'],
    onSubmit: async (formValues) => {
      const data: Record<string, unknown> = {
        channelPattern: formValues.channelPattern.trim(),
        operations: formValues.operations,
        endpointPath: formValues.endpointPath.trim(),
        endpointMethod: formValues.endpointMethod,
        active: formValues.active,
        priority: Number(formValues.priority),
        cooldownMs: Number(formValues.cooldownMs),
        stopOnMatch: formValues.stopOnMatch,
        inputMapping: formValues.inputMapping,
      };

      if (mode === 'create') {
        data.name = formValues.name.trim();
      }

      if (formValues.description.trim()) {
        data.description = formValues.description.trim();
      } else if (mode === 'edit') {
        data.description = null;
      }

      if (formValues.entity.trim()) {
        data.entity = formValues.entity.trim();
      } else if (mode === 'edit') {
        data.entity = null;
      }

      if (formValues.source.trim()) {
        data.source = formValues.source.trim();
      } else if (mode === 'edit') {
        data.source = null;
      }

      if (formValues.excludeSources.length > 0) {
        data.excludeSources = formValues.excludeSources;
      } else {
        data.excludeSources = [];
      }

      await onSubmit(data);
    },
    validate: (vals) => {
      const errs: Partial<Record<keyof MessageRouteFormData, string>> = {};

      if (mode === 'create') {
        if (!vals.name.trim()) {
          errs.name = 'Name is required';
        } else if (vals.name.length > 100) {
          errs.name = 'Name must be 100 characters or less';
        }
      }

      if (!vals.channelPattern.trim()) {
        errs.channelPattern = 'Channel pattern is required';
      }

      if (vals.operations.length === 0) {
        errs.operations = 'At least one operation is required';
      }

      if (!vals.endpointPath.trim()) {
        errs.endpointPath = 'Endpoint path is required';
      }

      const priorityNum = Number(vals.priority);
      if (vals.priority !== '' && (isNaN(priorityNum) || priorityNum < 0)) {
        errs.priority = 'Priority must be a positive number';
      }

      const cooldownNum = Number(vals.cooldownMs);
      if (vals.cooldownMs !== '' && (isNaN(cooldownNum) || cooldownNum < 0)) {
        errs.cooldownMs = 'Cooldown must be a positive number (milliseconds)';
      }

      return errs;
    },
  });

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/message-routes');
    }
  };

  // Build the plain-English preview summary
  const buildPreviewSummary = () => {
    const parts: string[] = [];

    if (values.channelPattern) {
      parts.push(`Routes messages on \`${values.channelPattern}\` channels`);
    } else {
      parts.push('Routes messages');
    }

    const conditions: string[] = [];
    if (values.entity) conditions.push(`entity is \`${values.entity}\``);
    if (values.operations.length > 0) {
      const opStr = values.operations.map((o) => `\`${o}\``).join(', ');
      conditions.push(`operation is ${values.operations.length > 1 ? `one of [${opStr}]` : opStr}`);
    }
    if (values.source) conditions.push(`source is \`${values.source}\``);

    if (conditions.length > 0) {
      parts.push(`where ${conditions.join(' AND ')}`);
    }

    if (values.endpointPath) {
      parts.push(`sending a ${values.endpointMethod} to \`${values.endpointPath}\``);
    }

    const meta: string[] = [];
    if (values.priority !== '') meta.push(`priority ${values.priority}`);
    if (values.cooldownMs !== '' && Number(values.cooldownMs) > 0) {
      meta.push(`cooldown ${Math.round(Number(values.cooldownMs) / 1000)}s`);
    }
    if (meta.length > 0) {
      parts.push(`(${meta.join(', ')})`);
    }

    return parts.join(', ');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Left column - form fields */}
        <div className="space-y-8">
          {/* Section 1: Identity */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Identity
            </h3>
            <div className="space-y-4">
              {mode === 'create' && (
                <FormField
                  label="Name"
                  name="name"
                  type="text"
                  value={values.name}
                  onChange={handleChange}
                  error={errors.name}
                  placeholder="e.g., enrich-new-contacts"
                  required
                />
              )}
              <FormTextArea
                label="Description"
                name="description"
                value={values.description}
                onChange={handleChange}
                error={errors.description}
                placeholder="Describe what this route does..."
                rows={2}
              />
              <div className="flex items-center gap-6">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="active"
                    name="active"
                    checked={values.active}
                    onChange={(e) => setFieldValue('active', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Active
                  </label>
                </div>
                <FormField
                  label="Priority"
                  name="priority"
                  type="number"
                  value={values.priority}
                  onChange={handleChange}
                  error={errors.priority}
                  placeholder="100"
                  min={0}
                  className="w-32"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Matching Conditions */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Matching Conditions
            </h3>
            <div className="space-y-4">
              <FormField
                label="Channel Pattern"
                name="channelPattern"
                type="text"
                value={values.channelPattern}
                onChange={handleChange}
                error={errors.channelPattern}
                placeholder="e.g., contacts:* or project:123:*"
                required
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
                Glob pattern matching incoming message channels. Use <code className="font-mono px-1 bg-gray-100 dark:bg-gray-700 rounded">*</code> to match any segment.
              </p>
              <FormField
                label="Entity"
                name="entity"
                type="text"
                value={values.entity}
                onChange={handleChange}
                error={errors.entity}
                placeholder="Leave blank to match any entity type"
              />
              <OperationsTagInput
                label="Operations"
                value={values.operations}
                onChange={(ops) => setFieldValue('operations', ops)}
                placeholder="Type and press Enter (e.g., create, update)"
                suggestions={COMMON_OPERATIONS}
                error={errors.operations}
                required
              />
              <FormField
                label="Source"
                name="source"
                type="text"
                value={values.source}
                onChange={handleChange}
                error={errors.source}
                placeholder="Leave blank to match any source"
              />
              <OperationsTagInput
                label="Exclude Sources"
                value={values.excludeSources}
                onChange={(srcs) => setFieldValue('excludeSources', srcs)}
                placeholder="Sources to ignore even when other conditions match"
              />
            </div>
          </div>

          {/* Section 3: Target */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Target
            </h3>
            <div className="space-y-4">
              <FormField
                label="Endpoint Path"
                name="endpointPath"
                type="text"
                value={values.endpointPath}
                onChange={handleChange}
                error={errors.endpointPath}
                placeholder="e.g., enrich-contact"
                required
              />
              <FormSelect
                label="Endpoint Method"
                name="endpointMethod"
                value={values.endpointMethod}
                onChange={handleChange}
                options={ENDPOINT_METHOD_OPTIONS}
                error={errors.endpointMethod}
              />
              <FormField
                label="Cooldown (ms)"
                name="cooldownMs"
                type="number"
                value={values.cooldownMs}
                onChange={handleChange}
                error={errors.cooldownMs}
                placeholder="60000"
                min={0}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
                Minimum milliseconds between dispatches for the same entity. Prevents loops. Default: 60000 (60s).
              </p>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="stopOnMatch"
                  name="stopOnMatch"
                  checked={values.stopOnMatch}
                  onChange={(e) => setFieldValue('stopOnMatch', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="stopOnMatch" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Stop on match
                </label>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  (stop evaluating further routes after this one fires)
                </span>
              </div>
            </div>
          </div>

          {/* Section 4: Input Mapping */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
              Input Mapping
            </h3>
            <InputMappingEditor
              value={values.inputMapping}
              onChange={(mapping) => setFieldValue('inputMapping', mapping)}
              error={errors.inputMapping as string | undefined}
            />
          </div>
        </div>

        {/* Right column - preview */}
        <div className="space-y-6">
          <div className="bg-gray-50 dark:bg-gray-900 p-5 rounded-lg border border-gray-200 dark:border-gray-700 sticky top-6">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Route Preview</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed italic">
              {buildPreviewSummary() || 'Fill in the fields to see a preview...'}
            </p>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
              {mode === 'create' && values.name && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Name</span>
                  <span className="text-gray-900 dark:text-white font-medium truncate max-w-[200px]">{values.name}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Status</span>
                <span>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      values.active
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                    }`}
                  >
                    {values.active ? 'Active' : 'Inactive'}
                  </span>
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Priority</span>
                <span className="text-gray-900 dark:text-white">{values.priority}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Method</span>
                <span className="text-gray-900 dark:text-white font-mono">{values.endpointMethod}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Stop on match</span>
                <span className="text-gray-900 dark:text-white">{values.stopOnMatch ? 'Yes' : 'No'}</span>
              </div>
              {values.operations.length > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Operations</span>
                  <span className="text-gray-900 dark:text-white">{values.operations.join(', ')}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Mapped fields</span>
                <span className="text-gray-900 dark:text-white">{Object.keys(values.inputMapping).length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SmartFormActions
        mode={mode}
        isDirty={isDirty}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onCancel={handleCancel}
        submitLabel={mode === 'create' ? 'Create Route' : 'Save Changes'}
      />
    </form>
  );
}
